
import { LANGUAGES as SUPPORTED_LANGUAGES } from '../../lib/languages';
import type { PlacementTiming } from './types';

export function mmss(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function seconds(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '–';
  const value = ms / 1000;
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} с`.replace('.', ',');
}

export function timeLabel(ms: number, timing: PlacementTiming): string {
  return timing === 'exact' ? mmss(ms) : `≈ ${mmss(ms)}`;
}

export const DENSITY_PROFILES: readonly { value: string; label: string; hint: string }[] = [
  { value: 'default', label: 'Обычные вставки', hint: '2–5 с, горизонталь и длинные ролики' },
  { value: 'short_form', label: 'Короткие вставки', hint: '0,8–2 с, вертикаль и клипы' },
  { value: 'legacy', label: 'Длинные вставки', hint: '1,5–5 с, старые проекты' },
];

export function profileLabel(value: string): string {
  return DENSITY_PROFILES.find((profile) => profile.value === value)?.label ?? value;
}

export const LANGUAGES: readonly { value: string; label: string }[] = SUPPORTED_LANGUAGES.map(
  (item) => ({ value: item.code, label: item.label }),
);

const QUERY_SOURCE: Readonly<Record<string, string>> = {
  llm: 'запрос модели',
  llm_alt: 'другая идея кадра',
  llm_en: 'запрос по-английски',
};

export function querySourceLabel(value: string): string {
  return QUERY_SOURCE[value] ?? value;
}

export function resolution(width: number | null, height: number | null): string | null {
  if (!width || !height) return null;
  return `${width}×${height}`;
}

export function countParagraphs(text: string): number {
  return text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean).length;
}

export function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

export const MAX_TEXT_CHARS = 60_000;

export function contextPayload(value: string): string | undefined {
  return value.trim() || undefined;
}

export const HIGH_ALIGNMENT_LOSS = 1.0;

export function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(count) % 100;
  const mod10 = Math.abs(count) % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export function segmentWord(count: number): string {
  return plural(count, 'сегмент', 'сегмента', 'сегментов');
}

export function paragraphWord(count: number): string {
  return plural(count, 'абзац', 'абзаца', 'абзацев');
}

export function insertWord(count: number): string {
  return plural(count, 'вставка', 'вставки', 'вставок');
}

export function placeWord(count: number): string {
  return plural(count, 'место', 'места', 'мест');
}

export function fileSize(bytes: number): string {
  const mb = bytes / 1024 ** 2;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} ГБ`.replace('.', ',');
  if (mb >= 1) return `${Math.round(mb)} МБ`;
  return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
}

const REJECTION_PHRASES: Readonly<Record<string, string>> = {
  malformed: 'Место пришло в неразборчивом виде',
  unknown_segment: 'Место указано в несуществующем абзаце',
  index_out_of_range: 'Номер слова за пределами сценария',
  span_out_of_limits: 'Кусок текста не той длины',
  echo_mismatch: 'Не удалось привязать вставку к этому месту',
  ambiguous_repair: 'Слово повторяется рядом — непонятно, какое имелось в виду',
  overlap: 'Место налезает на соседнюю вставку',
  density_budget: 'Вставок рядом и так достаточно',
  hook_zone: 'Слишком близко к началу — там держим зрителя',
};

export function rejectionLabel(reason: string): string {
  return REJECTION_PHRASES[reason] ?? 'Вставку сюда поставить не удалось';
}

export function placeAddress(segmentIndex: number | null, startWord: number | null): string {
  const parts: string[] = [];
  if (segmentIndex !== null) parts.push(`абзац ${segmentIndex + 1}`);
  if (startWord !== null) parts.push(`слово №${startWord + 1}`);
  return parts.join(', ');
}
